<script setup lang="ts">
import { defineProps, ref, defineEmits } from "vue";
import CardLoader from "@/components/Loaders/CardLoader.vue";
import TechnicalDetailsInputsBlock from "../Commercial/TechnicalDetailsInputsBlock.vue";
import TechnicalModal from "../Commercial/TechnicalModal.vue";
import { BauabschnittDetail } from "@/types";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isLoading: boolean;
  projectId: string | null | undefined;
  vnb: string | null | undefined;
  wb: string | null | undefined;
  wechselrichter: string | null | undefined;
  speicher: string | null | undefined;
  masskonzept: string | null | undefined;
  numberOfModules: number | null | undefined;
  modulType: string | null | undefined;
  ibnDate: Date | null | undefined;
  homeOwnerName: string | null | undefined;
  homeOwnerAddress: string | null | undefined;
  homeOwnerPhone: string | null | undefined;
  emailAddress: string | null | undefined;
  bauabschnitt: number | null | undefined;
  strings: number | null | undefined;
  moduleFields: number | null | undefined;
  bauabschnittDetails: BauabschnittDetail[] | null | undefined;
};

type Emits = {
  (event: "refresh"): void;
};

const props = defineProps<Props>();
defineEmits<Emits>();
const { t } = useTranslate();
const isTechnicalDetailsInput = ref(
  props.bauabschnitt && props.bauabschnitt > 0
);
const isTechnicalFormModal = ref(false);

const toggleTechnicalFormModal = () => {
  isTechnicalFormModal.value = !isTechnicalFormModal.value;
};

const selectedInput = ref();
const onSelectedInputHandler = (input: any) => {
  selectedInput.value = input;
  isTechnicalFormModal.value = true;
};
</script>

<template>
  <!-- Technical form modal -->
  <Transition name="nested">
    <TechnicalModal
      v-if="isTechnicalFormModal"
      class="modal"
      @toggle-modal="toggleTechnicalFormModal"
      :selected-input="selectedInput"
      :project-id="props.projectId"
      @refresh="$emit('refresh')"
    />
  </Transition>

  <!-- Details container -->
  <div
    class="w-full mb-3 flex flex-col lg:flex-row items-start gap-3 h-[285px]"
  >
    <!-- Technical details -->
    <div
      class="w-full lg:w-[72%] h-full bg-white p-3 rounded-primary-radius shadow-[0px_1px_13px_rgba(57,56,64,0.1)] overflow-auto"
    >
      <div class="flex items-center justify-between mb-2">
        <h2
          class="text-xl text-primary-text-color tracking-[0.01rem] font-semibold"
        >
          {{ t("projects.technical_details") }}
        </h2>
        <div
          v-if="bauabschnitt && bauabschnitt > 0"
          class="flex items-start gap-2"
        >
          <button
            @click="isTechnicalDetailsInput = true"
            class="w-6 h-6 flex items-center justify-center rounded-sm"
          >
            <img
              v-show="isTechnicalDetailsInput"
              src="@/assets/arrow-left-gray.svg"
              alt=""
            />
            <img
              v-show="!isTechnicalDetailsInput"
              src="@/assets/arrow-left.svg"
              alt=""
            />
          </button>

          <button
            @click="isTechnicalDetailsInput = false"
            class="w-6 h-6 flex items-center justify-center cursor-pointer rounded-sm"
          >
            <img
              v-show="isTechnicalDetailsInput"
              src="@/assets/arrow-right.svg"
              alt=""
            />
            <img
              v-show="!isTechnicalDetailsInput"
              src="@/assets/arrow-right-gray.svg"
              alt=""
            />
          </button>
        </div>
      </div>
      <CardLoader v-if="isLoading" />
      <div v-else>
        <div v-if="isTechnicalDetailsInput">
          <TechnicalDetailsInputsBlock
            @open-modal="onSelectedInputHandler"
            :strings="strings"
            :module-fields="moduleFields"
            :bauabschnitt="bauabschnitt"
            :bauabschnitt-details="bauabschnittDetails"
          />
        </div>
        <div v-else class="flex flex-col lg:flex-row items-start gap-3">
          <!-- Left -->
          <div class="flex-1 flex flex-col items-start gap-2">
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                VNB
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ vnb ? vnb : "-" }}
              </p>
            </div>
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                Wechselrichter
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ wechselrichter ? wechselrichter : "-" }}
              </p>
            </div>
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                Speicher
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ speicher ? speicher : "-" }}
              </p>
            </div>
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                Masskonzept
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ masskonzept ? masskonzept : "-" }}
              </p>
            </div>
          </div>
          <!-- Right -->
          <div class="flex-1 flex flex-col items-start gap-2">
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                WB
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ wb ? wb : "-" }}
              </p>
            </div>
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                Number of Modules
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ numberOfModules ? numberOfModules : "-" }}
              </p>
            </div>
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                Modul Type
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ modulType ? modulType : "-" }}
              </p>
            </div>
            <div>
              <h4
                class="text-sm font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
              >
                IBN Date
              </h4>
              <p
                class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-normal pt-1"
              >
                {{ ibnDate ? new Date(ibnDate).toLocaleDateString("de") : "-" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Details -->
    <div
      class="w-[35%] h-[285px] bg-white p-3 rounded-primary-radius shadow-[0px_1px_13px_rgba(57,56,64,0.1)]"
    >
      <h2
        class="text-xl pb-4 text-primary-text-color tracking-[0.01rem] font-semibold"
      >
        {{ t("projects.details") }}
      </h2>
      <CardLoader v-if="isLoading" />
      <div v-else class="flex flex-col items-start gap-4">
        <div
          class="flex items-center gap-2 pb-2 border-b border-[#F1F0F5] w-full"
        >
          <img
            class="w-4 h-4"
            src="@/assets/user-details.svg"
            alt="User icon"
          />
          <span
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
            >{{ homeOwnerName ? homeOwnerName : "-" }}</span
          >
        </div>
        <div
          class="flex items-center gap-2 pb-2 border-b border-[#F1F0F5] w-full"
        >
          <img class="w-4 h-4" src="@/assets/phone-icon.svg" alt="User icon" />
          <a
            v-if="homeOwnerPhone"
            :href="'tel:' + homeOwnerPhone"
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
            >{{ homeOwnerPhone }}</a
          >
          <span
            v-else
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
            >-</span
          >
        </div>
        <div
          class="flex items-center gap-2 pb-2 border-b border-[#F1F0F5] w-full"
        >
          <img
            class="w-4 h-4"
            src="@/assets/message-icon.svg"
            alt="User icon"
          />
          <a
            v-if="emailAddress"
            :href="'mailto:' + emailAddress"
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
          >
            {{ emailAddress }}
          </a>
          <span
            v-else
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
            >-</span
          >
        </div>
        <div
          class="flex items-center gap-2 pb-2 border-b border-[#F1F0F5] w-full"
        >
          <img class="w-4 h-4" src="@/assets/navigation.svg" alt="User icon" />
          <a
            v-if="homeOwnerAddress"
            :href="'https://www.google.com/maps?q=' + homeOwnerAddress"
            target="_blank"
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
            >{{ homeOwnerAddress }}</a
          >
          <span
            v-else
            class="text-base leading-[20px] tracking-[0.01rem] text-primary-text-color font-norma"
            >-</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.nested-enter-from,
.nested-leave-to {
  opacity: 0;
}

.nested-enter-active .modal,
.nested-leave-active .modal {
  transition: transform 0.3s ease-in-out;
}

.nested-enter-from .modal,
.nested-leave-to .modal {
  transform: translateY(100%);
}

.nested-enter-to .modal,
.nested-leave-from .modal {
  transform: translateY(0);
}
</style>
